import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { Game } from '../types/game';
import { OtpSession } from '../types/api';
import { SubscriptionPlan } from '../types/subscription';
import {
  StoredSubscription,
  getActiveSubscription,
  saveSubscription,
  clearSubscription,
} from '../utils/subscriptionStorage';

interface AuthContextType {
  isModalOpen: boolean;
  pendingGame: Game | null;
  verifiedPhone: string | null;
  otpSession: OtpSession | null;
  isSubscribed: boolean;
  activeSubscription: StoredSubscription | null;
  openLoginForGame: (game: Game) => void;
  closeLogin: () => void;
  setVerifiedPhone: (phone: string) => void;
  setOtpSession: (session: OtpSession | null) => void;
  activateSubscription: (plan: SubscriptionPlan, msisdn: string) => void;
  canPlayGame: () => boolean;
  clearPendingGame: () => void;
  clearAuthSession: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const getInitialAuthState = () => {
  const subscription = getActiveSubscription();
  return {
    isSubscribed: !!subscription,
    verifiedPhone: subscription?.msisdn ?? null,
    activeSubscription: subscription,
  };
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const initialState = getInitialAuthState();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pendingGame, setPendingGame] = useState<Game | null>(null);
  const [verifiedPhone, setVerifiedPhoneState] = useState<string | null>(
    initialState.verifiedPhone
  );
  const [otpSession, setOtpSessionState] = useState<OtpSession | null>(null);
  const [isSubscribed, setIsSubscribedState] = useState<boolean>(initialState.isSubscribed);
  const [activeSubscription, setActiveSubscriptionState] = useState<StoredSubscription | null>(
    initialState.activeSubscription
  );

  const openLoginForGame = (game: Game) => {
    setPendingGame(game);
    setIsModalOpen(true);
  };

  const closeLogin = () => {
    setIsModalOpen(false);
  };

  const setVerifiedPhone = (phone: string) => {
    setVerifiedPhoneState(phone);
  };

  const setOtpSession = (session: OtpSession | null) => {
    setOtpSessionState(session);
  };

  const activateSubscription = (plan: SubscriptionPlan, msisdn: string) => {
    const subscription = saveSubscription(plan, msisdn);
    setVerifiedPhoneState(msisdn);
    setIsSubscribedState(true);
    setActiveSubscriptionState(subscription);
  };

  const canPlayGame = useCallback((): boolean => {
    return getActiveSubscription() !== null;
  }, []);

  const clearPendingGame = () => {
    setPendingGame(null);
  };

  const clearAuthSession = () => {
    setIsModalOpen(false);
    setPendingGame(null);
    setVerifiedPhoneState(null);
    setOtpSessionState(null);
    setIsSubscribedState(false);
    setActiveSubscriptionState(null);
    clearSubscription();
  };

  return (
    <AuthContext.Provider
      value={{
        isModalOpen,
        pendingGame,
        verifiedPhone,
        otpSession,
        isSubscribed,
        activeSubscription,
        openLoginForGame,
        closeLogin,
        setVerifiedPhone,
        setOtpSession,
        activateSubscription,
        canPlayGame,
        clearPendingGame,
        clearAuthSession,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
